// CustomersAsExtensions - Customer management that works without logout issues
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  Extension as ExtensionIcon,
  LocalPhone as LocalPhoneIcon,
  Inventory as PackageIcon,
  Settings as SettingsIcon,
  VpnKey as VpnKeyIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ManageAccounts as ManageAccountsIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useCustomerFilterContext } from '../../contexts/CustomerFilterContext';
import api from '../../services/api';
// customerViewService import removed - using sessionStorage directly

interface Customer {
  id: number;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  status: 'Active' | 'Disabled';
  memberCount: number;
  extensionCount: number;
  didCount: number;
  packageName: string;
  monthlyPrice: number;
  totalMonthlyRate: number;
  managedBy?: string;
}

function CustomersAsExtensions() {
  const { member } = useAuth();
  const navigate = useNavigate();
  const { setCustomerFilter } = useCustomerFilterContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [resellerFilter, setResellerFilter] = useState<string>('all');
  const [resellers, setResellers] = useState<{id: string, name: string}[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Available packages for dropdown
  const [packages, setPackages] = useState<any[]>([]);

  // Tab state for edit dialog
  const [editDialogTab, setEditDialogTab] = useState(0);

  // Package selector state
  const [showPackageSelector, setShowPackageSelector] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState('');

  // Addon services state
  const [addonServices, setAddonServices] = useState<any[]>([]);
  const [showAddonSelector, setShowAddonSelector] = useState(false);
  const [selectedAddonId, setSelectedAddonId] = useState('');

  // Internet services state
  const [internetServices, setInternetServices] = useState<any[]>([]);
  const [showInternetSelector, setShowInternetSelector] = useState(false);
  const [selectedInternetId, setSelectedInternetId] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    packageId: '',
    status: 'Active',
    billingType: 'prepaid',
    paymentTerms: '30',
  });

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/endpoints/customers');
      if (response.data.success) {
        setCustomers(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch customers:', err);
      setError(err.response?.data?.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      console.log('Fetching packages from /endpoints/packages');
      const response = await api.get('/endpoints/packages');
      console.log('Packages response:', response.data);
      if (response.data.success && response.data.data?.packages) {
        const packagesData = response.data.data.packages;
        console.log('Packages data:', packagesData);
        console.log('First package:', packagesData[0]);
        setPackages(packagesData);
      } else if (response.data.success && response.data.packages) {
        // Fallback to direct packages field
        const packagesData = response.data.packages;
        console.log('Packages data (fallback):', packagesData);
        setPackages(packagesData);
      } else {
        console.log('No packages in response or not successful');
        setPackages([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch packages:', err);
      setPackages([]); // Set empty array on error
    }
  };

  const fetchAddonServices = async () => {
    try {
      const response = await api.get('/endpoints/addonservices');
      console.log('Addon services response:', response.data);

      // Handle different response structures - based on AddOnServices.tsx
      if (response.data) {
        if (response.data.success && response.data.data?.services) {
          // This is the structure used by AddOnServices component
          setAddonServices(response.data.data.services);
        } else if (Array.isArray(response.data)) {
          // Direct array response
          setAddonServices(response.data);
        } else if (response.data.success === false) {
          // API returned an error
          console.error('Addon services API error:', response.data.message);
          setAddonServices([]);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Wrapped in data property
          setAddonServices(response.data.data);
        } else if (response.data.services && Array.isArray(response.data.services)) {
          // Wrapped in services property
          setAddonServices(response.data.services);
        } else {
          console.log('Unexpected addon services response structure:', response.data);
          setAddonServices([]);
        }
      } else {
        setAddonServices([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch addon services:', err);
      console.error('Error details:', err.response?.data);
      setAddonServices([]);
      // Don't throw the error - just log it
    }
  };

  const fetchInternetServices = async () => {
    try {
      const response = await api.get('/endpoints/internetservices');
      console.log('Internet services response:', response.data);

      // Handle different response structures
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          setInternetServices(response.data);
        } else if (response.data.success === false) {
          // API returned an error
          console.error('Internet services API error:', response.data.message);
          setInternetServices([]);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Wrapped in data property
          setInternetServices(response.data.data);
        } else if (response.data.services && Array.isArray(response.data.services)) {
          // Wrapped in services property
          setInternetServices(response.data.services);
        } else {
          console.log('Unexpected internet services response structure:', response.data);
          setInternetServices([]);
        }
      } else {
        setInternetServices([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch internet services:', err);
      console.error('Error details:', err.response?.data);
      setInternetServices([]);
      // Don't throw the error - just log it
    }
  };

  // Handle addon service assignment
  const handleAssignAddonService = async () => {
    if (!selectedAddonId || !selectedCustomer?.id) return;

    try {
      // TODO: Call API to assign addon service to customer
      console.log(`Assigning addon service ${selectedAddonId} to customer ${selectedCustomer.id}`);

      // For now, show success message
      setSuccess(`Addon service assigned successfully to ${selectedCustomer.companyName}`);

      // Reset selector
      setShowAddonSelector(false);
      setSelectedAddonId('');

      // TODO: Refresh customer data to show the newly assigned service
    } catch (err: any) {
      console.error('Failed to assign addon service:', err);
      setError('Failed to assign addon service');
    }
  };

  // Handle internet service assignment
  const handleAssignInternetService = async () => {
    if (!selectedInternetId || !selectedCustomer?.id) return;

    try {
      // TODO: Call API to assign internet service to customer
      console.log(`Assigning internet service ${selectedInternetId} to customer ${selectedCustomer.id}`);

      // For now, show success message
      setSuccess(`Internet service assigned successfully to ${selectedCustomer.companyName}`);

      // Reset selector
      setShowInternetSelector(false);
      setSelectedInternetId('');

      // TODO: Refresh customer data to show the newly assigned service
    } catch (err: any) {
      console.error('Failed to assign internet service:', err);
      setError('Failed to assign internet service');
    }
  };

  // Handle add customer
  const handleAddCustomer = async () => {
    try {
      setLoading(true);
      const response = await api.post('/endpoints/customers', formData);
      
      if (response.data.success) {
        setSuccess('Customer added successfully');
        setAddDialogOpen(false);
        fetchCustomers();
        // Reset form
        setFormData({
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          packageId: '',
          status: 'Active',
          billingType: 'prepaid',
          paymentTerms: '30',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit customer
  const handleEditCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      setLoading(true);
      
      // Build update payload - exclude packageId since it's not handled by the update endpoint
      const updatePayload: any = {
        companyName: formData.companyName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || '',
        status: formData.status
      };
      
      console.log('Sending update for customer ID:', selectedCustomer.id);
      console.log('Update payload:', updatePayload);
      
      const response = await api.put(`/endpoints/customers?id=${selectedCustomer.id}`, updatePayload);
      
      if (response.data.success) {
        setSuccess('Customer updated successfully');
        setEditDialogOpen(false);
        setSelectedCustomer(null);
        // Reset form data
        setFormData({
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          packageId: '',
          status: 'Active',
          billingType: 'prepaid',
          paymentTerms: '30',
        });
        fetchCustomers();
      }
    } catch (err: any) {
      console.error('Update failed:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete customer
  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      setLoading(true);
      const response = await api.delete(`/endpoints/customers?id=${selectedCustomer.id}`);
      
      if (response.data.success) {
        setSuccess('Customer deleted successfully');
        setDeleteDialogOpen(false);
        fetchCustomers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle customer status
  const handleToggleStatus = async (customer: Customer) => {
    try {
      setLoading(true);
      const newStatus = customer.status === 'Active' ? 'Disabled' : 'Active';
      const response = await api.put(`/endpoints/customers?id=${customer.id}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        setSuccess(`Customer ${newStatus === 'Active' ? 'enabled' : 'disabled'} successfully`);
        fetchCustomers();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update customer status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (member && (member.memberType === 'master_admin' || member.memberType === 'reseller_admin')) {
      fetchCustomers();
      fetchPackages().catch(err => {
        console.error('Failed to fetch packages:', err);
        // Don't let package fetch failure break the component
      });
      // Fetch addon and internet services only when their buttons are clicked
      // to avoid unnecessary API calls and potential auth errors
    }
  }, [member]);

  // Extract unique resellers from customers
  useEffect(() => {
    const uniqueResellers = new Map<string, string>();
    customers.forEach(customer => {
      if (customer.managedBy && customer.managedBy !== 'Direct') {
        // Use managedBy as both key and value for now
        uniqueResellers.set(customer.managedBy, customer.managedBy);
      }
    });
    const resellerList = Array.from(uniqueResellers, ([id, name]) => ({ id, name }));
    setResellers(resellerList.sort((a, b) => a.name.localeCompare(b.name)));
  }, [customers]);

  // Filter customers locally - exactly like Extensions/Users
  const filteredCustomers = (Array.isArray(customers) ? customers : []).filter(customer => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = customer.id.toString().includes(searchTerm) ||  // Search by ID
                           customer.companyName?.toLowerCase().includes(searchLower) ||
                           customer.email?.toLowerCase().includes(searchLower) ||
                           customer.firstName?.toLowerCase().includes(searchLower) ||
                           customer.lastName?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Apply reseller filter
    if (resellerFilter !== 'all') {
      if (resellerFilter === 'direct') {
        return customer.managedBy === 'Direct' || !customer.managedBy;
      } else {
        return customer.managedBy === resellerFilter;
      }
    }
    
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">
            Customers Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({
                companyName: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                packageId: '',
                status: 'Active',
                billingType: 'prepaid',
                paymentTerms: '30',
              });
              setAddDialogOpen(true);
            }}
          >
            Add Customer
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCustomers}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by ID, name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: '300px' }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Partner/Reseller</InputLabel>
            <Select
              value={resellerFilter}
              onChange={(e) => setResellerFilter(e.target.value)}
              label="Partner/Reseller"
            >
              <MenuItem value="all">All Customers</MenuItem>
              <MenuItem value="direct">Direct (No Reseller)</MenuItem>
              <Divider />
              {resellers.map(reseller => (
                <MenuItem key={reseller.id} value={reseller.name}>
                  {reseller.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Actions</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Package</TableCell>
              <TableCell>Resources</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Managed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {searchTerm ? 'No customers found matching your search' : 'No customers found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  {/* Actions - FIRST */}
                  <TableCell>
                    <Tooltip title="Login as Admin">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={async () => {
                          // Create display name for the tab title
                          const displayName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                          console.log('[CustomersAsExtensions] Login as customer clicked:', { id: customer.id, name: displayName });
                          
                          try {
                            // Store intent on server and get secure token
                            const response = await api.post('/endpoints/login_as_customer', {
                              customerId: customer.id,
                              customerName: displayName
                            });
                            
                            console.log('[CustomersAsExtensions] Server response:', response.data);
                            
                            if (response.data.success && response.data.data?.token) {
                              const token = response.data.data.token;
                              const url = `/extensions?lat=${token}`;
                              console.log('[CustomersAsExtensions] Opening new tab with URL:', url);
                              // Open new tab with only the secure token
                              window.open(url, '_blank');
                            } else {
                              console.error('[CustomersAsExtensions] Failed to create login-as token');
                              setError('Failed to create login session');
                            }
                          } catch (error: any) {
                            console.error('[CustomersAsExtensions] Error creating login-as session:', error);
                            setError(error.response?.data?.message || 'Failed to create login session');
                          }
                        }}
                      >
                        <LoginIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setFormData({
                            companyName: customer.companyName,
                            firstName: customer.firstName,
                            lastName: customer.lastName,
                            email: customer.email,
                            phone: customer.phone || '',
                            packageId: '', // Will need to get package ID
                            status: customer.status,
                            billingType: 'prepaid', // TODO: Get from customer data
                            paymentTerms: '30', // TODO: Get from customer data
                          });
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  {/* ID */}
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {customer.id}
                    </Typography>
                  </TableCell>
                  {/* Company */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      {customer.companyName}
                    </Box>
                  </TableCell>
                  {/* Contact */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Box>
                        {customer.firstName} {customer.lastName}
                        {customer.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {customer.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {/* Email */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {customer.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Click to manage packages">
                      <Chip 
                        icon={<PackageIcon />}
                        label={customer.packageName || 'No Package'} 
                        size="small"
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { 
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main' 
                          }
                        }}
                        onClick={() => {
                          const displayName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                          setCustomerFilter(customer.id.toString(), displayName);
                          navigate(`/service-packages?customerId=${customer.id}&customerName=${encodeURIComponent(displayName)}`);
                        }}
                      />
                    </Tooltip>
                    {customer.monthlyPrice > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <MoneyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          ${customer.totalMonthlyRate || customer.monthlyPrice}/mo
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title="Click to manage users">
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.7 }
                          }}
                          onClick={() => {
                            const displayName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                            setCustomerFilter(customer.id.toString(), displayName);
                            navigate(`/users?customerId=${customer.id}&customerName=${encodeURIComponent(displayName)}`);
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                            {customer.memberCount || 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip title="Click to manage extensions">
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.7 }
                          }}
                          onClick={() => {
                            const displayName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                            setCustomerFilter(customer.id.toString(), displayName);
                            navigate(`/extensions?customerId=${customer.id}&customerName=${encodeURIComponent(displayName)}`);
                          }}
                        >
                          <ExtensionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                            {customer.extensionCount || 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip title="Click to manage phone numbers">
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.7 }
                          }}
                          onClick={() => {
                            const displayName = customer.companyName || `${customer.firstName} ${customer.lastName}`;
                            setCustomerFilter(customer.id.toString(), displayName);
                            navigate(`/phone-numbers?customerId=${customer.id}&customerName=${encodeURIComponent(displayName)}`);
                          }}
                        >
                          <LocalPhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                            {customer.didCount || 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={customer.status === 'Active' ? <CheckCircleIcon /> : <BlockIcon />}
                      label={customer.status} 
                      size="small"
                      color={customer.status === 'Active' ? 'success' : 'default'}
                      variant="outlined"
                      onClick={() => handleToggleStatus(customer)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  {/* Managed By - LAST */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {customer.managedBy || 'Direct'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Customer Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Service Package</InputLabel>
                <Select
                  value={formData.packageId}
                  label="Service Package"
                  onChange={(e) => setFormData({ ...formData, packageId: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.PackageID || pkg.id} value={pkg.PackageID || pkg.id}>
                      {pkg.PackageName || pkg.name} - ${pkg.MonthlyPrice || pkg.monthlyPrice}/mo
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCustomer} variant="contained" disabled={loading}>
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditDialogTab(0); // Reset to first tab when closing
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <Tabs
            value={editDialogTab}
            onChange={(e, newValue) => setEditDialogTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="General Information" />
            <Tab label="Packages & Billing" />
            <Tab label="Services & DIDs" />
            <Tab label="Activity" />
          </Tabs>

          {/* Tab Panel 0: General Information */}
          {editDialogTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Tab Panel 1: Packages & Billing */}
          {editDialogTab === 1 && (
            <Grid container spacing={2}>
              {/* Rate Summary at the top */}
              {selectedCustomer && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Current Billing Summary
                    </Typography>
                  </Grid>

                  {/* Total Summary Box */}
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Total Monthly Charges
                      </Typography>
                      <Typography variant="h4">
                        ${selectedCustomer.totalMonthlyRate || '0.00'}
                      </Typography>
                      <Typography variant="caption">
                        Includes all services and usage
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Rate Breakdown */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Package Rate
                      </Typography>
                      <Typography variant="h6">
                        ${selectedCustomer.monthlyPrice || '0.00'}
                      </Typography>
                      <Typography variant="caption">
                        {selectedCustomer.packageName}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={4}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        Extensions ({selectedCustomer.extensionCount || 0})
                      </Typography>
                      <Typography variant="body2">
                        Add'l: $14.95/ea
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={4}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        DIDs ({selectedCustomer.didCount || 0})
                      </Typography>
                      <Typography variant="body2">
                        Add'l: $6.95/ea
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                </>
              )}

              {/* Service Package Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Service Package
                </Typography>

                {/* Current Package Display */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Package:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {selectedCustomer?.packageName || 'No package assigned'}
                  </Typography>
                  {selectedCustomer?.monthlyPrice && (
                    <Typography variant="body2" color="text.secondary">
                      Base Rate: ${selectedCustomer.monthlyPrice}/month
                    </Typography>
                  )}
                </Box>

                {/* Add New Package Button */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PackageIcon />}
                  onClick={() => setShowPackageSelector(true)}
                  sx={{ mb: 2 }}
                >
                  Change Package
                </Button>

                {/* Package Selector */}
                {showPackageSelector && (
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Select Package</InputLabel>
                      <Select
                        value={selectedPackageId}
                        label="Select Package"
                        onChange={(e) => setSelectedPackageId(e.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        {packages.length === 0 && (
                          <MenuItem disabled>No packages available</MenuItem>
                        )}
                        {packages.map((pkg) => {
                          console.log('Rendering package:', pkg);
                          return (
                            <MenuItem key={pkg.PackageID || pkg.id} value={pkg.PackageID || pkg.id}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2">
                                  <strong>{pkg.PackageName || pkg.name}</strong> - ${pkg.MonthlyPrice || pkg.monthlyPrice}/mo
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Includes: {pkg.IncludedExtensions || 0} extensions, {pkg.IncludedDIDs || 0} DIDs
                                  {pkg.AdditionalExtenRate && ` • Extra ext: $${pkg.AdditionalExtenRate}`}
                                  {pkg.AdditionalDIDRate && ` • Extra DID: $${pkg.AdditionalDIDRate}`}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          // TODO: Implement package assignment
                          alert(`Would assign package ${selectedPackageId} to customer`);
                          setShowPackageSelector(false);
                          setSelectedPackageId('');
                        }}
                        disabled={!selectedPackageId}
                      >
                        Assign
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setShowPackageSelector(false);
                          setSelectedPackageId('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Add-on Services Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Add-on Services
                </Typography>

                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No add-on services assigned
                  </Typography>
                  {!showAddonSelector ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        try {
                          setShowAddonSelector(true);
                          if (addonServices.length === 0) {
                            fetchAddonServices().catch(err => {
                              console.error('Error fetching addon services:', err);
                              setError('Failed to load addon services');
                            });
                          }
                        } catch (err) {
                          console.error('Error in addon button click:', err);
                          setError('Failed to open addon selector');
                        }
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Service
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Add-on Service</InputLabel>
                        <Select
                          value={selectedAddonId}
                          label="Select Add-on Service"
                          onChange={(e) => setSelectedAddonId(e.target.value)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {addonServices.length === 0 && (
                            <MenuItem disabled>No add-on services available</MenuItem>
                          )}
                          {addonServices.map((service) => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.name}
                              {service.price && ` - $${service.price}`}
                              {(service.billingCycle || service.billing_cycle) && `/${service.billingCycle || service.billing_cycle}`}
                              {service.category && ` (${service.category})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleAssignAddonService}
                          disabled={!selectedAddonId}
                        >
                          Add
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowAddonSelector(false);
                            setSelectedAddonId('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Internet Services Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Internet Services
                </Typography>

                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No internet services assigned
                  </Typography>
                  {!showInternetSelector ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        try {
                          setShowInternetSelector(true);
                          if (internetServices.length === 0) {
                            fetchInternetServices().catch(err => {
                              console.error('Error fetching internet services:', err);
                              setError('Failed to load internet services');
                            });
                          }
                        } catch (err) {
                          console.error('Error in internet button click:', err);
                          setError('Failed to open internet selector');
                        }
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Internet Service
                    </Button>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Internet Service</InputLabel>
                        <Select
                          value={selectedInternetId}
                          label="Select Internet Service"
                          onChange={(e) => setSelectedInternetId(e.target.value)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {internetServices.length === 0 && (
                            <MenuItem disabled>No internet services available</MenuItem>
                          )}
                          {internetServices.map((service) => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.service_type}
                              {service.speed_option && ` - ${service.speed_option}`}
                              {service.monthly_price && ` - $${service.monthly_price}/month`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleAssignInternetService}
                          disabled={!selectedInternetId}
                        >
                          Add
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowInternetSelector(false);
                            setSelectedInternetId('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Billing Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Billing Type</InputLabel>
                  <Select
                    value={formData.billingType || 'prepaid'}
                    label="Billing Type"
                    onChange={(e) => setFormData({ ...formData, billingType: e.target.value })}
                  >
                    <MenuItem value="prepaid">Prepaid</MenuItem>
                    <MenuItem value="postpaid">Postpaid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    value={formData.paymentTerms || '30'}
                    label="Payment Terms"
                    onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                    disabled={formData.billingType !== 'postpaid'}
                  >
                    <MenuItem value="0">Due on Receipt</MenuItem>
                    <MenuItem value="15">NET 15</MenuItem>
                    <MenuItem value="30">NET 30</MenuItem>
                    <MenuItem value="45">NET 45</MenuItem>
                    <MenuItem value="60">NET 60</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Tab Panel 2: Services & DIDs */}
          {editDialogTab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Phone numbers and services information will be shown here
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* Tab Panel 3: Activity */}
          {editDialogTab === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Customer activity and logs will be shown here
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditDialogTab(0);
            setShowPackageSelector(false);
            setSelectedPackageId('');
          }}>Cancel</Button>
          <Button onClick={handleEditCustomer} variant="contained" disabled={loading}>
            Update Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete customer "{selectedCustomer?.companyName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will also delete all associated users, extensions, and settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCustomer} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomersAsExtensions;